import type { DocumentRecord, FarmDetail, FarmerDetail, FieldVisit, VisitPlan, VisitPlanTarget, VisitReason, VisitActivityDefinition, VisitActivityRecord, VisitPhotoEvidence, VisitFollowUp, HarvestDelivery, HarvestSeason, FarmerHarvestSeasonSummary, HarvestRegionSeasonSummary, HarvestPlotContext, InputCatalogueItem, InputDistribution, InventoryBalance, InventoryLocation, StaffDetail, StockMovement, Asset, AssetAssignment, AssetMaintenance, FiscalPeriod, LedgerAccount, JournalEntry, JournalLine, PaymentRequest, BuyingPrice, CommercialDelivery, CommercialTransaction, FarmerPayment, FarmerBalance, FarmerAdvance, FarmerAdvanceRecovery, FarmerAgreement, AgreementAmendment, GeographicUnit, NurseryVariety, NurserySite, NurseryBed, NurserySeedlingLot, NurseryPopulationAssessment, NurseryActivityDefinition, NurseryActivity, NurseryStockSummary, SeedlingDistributionPlan, SeedlingDistributionTarget, SeedlingDistributionSource, SeedlingDistributionVariety } from "@/types/domain";

export interface ApiList<T> { items: T[]; total: number; page: number; page_size: number; total_pages: number; }
export interface ApiFarmer { id: string; farmer_code: string; first_name: string; middle_name?: string; last_name: string; sex: NonNullable<FarmerDetail["sex"]>; nationality: string; marital_status?: string; education_level?: string; primary_occupation?: string; geographic_unit_id?: string; geographic_unit_label: string; farmer_group_id?: string; farmer_group_name?: string; birth_year_estimated?: number; assigned_field_officer?: string; assigned_agronomist?: string; farmer_status: FarmerDetail["farmerStatus"]; record_status: FarmerDetail["recordStatus"]; consent_obtained: boolean; consent_template_id?: string; primary_phone_last4?: string; national_id_last4?: string; household_size?: number; household_dependants?: number; dependants_in_household?: number; dependants_elsewhere?: number; dependants_under_18?: number; household_notes?: string; primary_next_of_kin_id?: string; primary_next_of_kin_first_name?: string; primary_next_of_kin_middle_name?: string; primary_next_of_kin_last_name?: string; primary_next_of_kin_relationship_type?: FarmerDetail["household"]["members"][number]["relationshipType"]; primary_next_of_kin_lives_in_household?: boolean; primary_next_of_kin_notes?: string; primary_next_of_kin_phone_last4?: string; pending_agreement_requirements?: number; initial_farm_id?: string; created_at?: string; row_version: number; }
export interface ApiFarmerGroup { id: string; group_code: string; group_name: string; geographic_unit_id: string; chairperson_name?: string; contact_phone?: string; registration_date?: string; status: string; }
export interface ApiConsentTemplate { id: string; consent_code: string; version: string; content_sha256: string; effective_from: string; exact_text?: string; }
export interface ApiFarm { id: string; farm_code: string; farmer_id: string; farmer_name: string; farm_name: string; geographic_unit_label: string; centre_latitude?: number; centre_longitude?: number; centre_accuracy_metres?: number; reported_area_acres?: number; gps_area_acres?: number; ownership_type?: string; boundary_captured: boolean; boundary_verified: boolean; verified_boundary_session_id?: string; mapping_session_id?: string; mapping_status?: string; mapping_verified_at?: string; seedling_eligible: boolean; farm_status: FarmDetail["farmStatus"]; record_status: FarmDetail["recordStatus"]; farmer_record_status: FarmDetail["recordStatus"]; row_version: number; }
export interface ApiPlot { id: string; plot_code: string; farm_id: string; plot_name: string; reported_area_acres?: number; gps_area_acres?: number; ownership_type?: string; current_land_use?: string; locality_name?: string; arise_contract_start_date?: string; arise_contract_end_date?: string; arise_contract_status?: "NOT_ENROLLED" | "ACTIVE" | "EXPIRING_SOON" | "COMPLETED" | "TERMINATED"; centre_latitude?: number; centre_longitude?: number; centre_accuracy_metres?: number; boundary_captured: boolean; boundary_verified: boolean; verified_boundary_session_id?: string; mapping_session_id?: string; mapping_status?: string; mapping_verified_at?: string; status: "ACTIVE" | "INACTIVE" | "ARCHIVED" | "VOID"; record_status: "DRAFT" | "SUBMITTED" | "APPROVED" | "REJECTED"; row_version: number; }
export interface ApiVisit { id: string; reference: string; visit_date: string; farmer_id: string; farm_id: string; plot_id?: string; plot_ids?: string[]; assigned_staff_id: string; observations: string; recommendations: string; pest_disease_observation?: string; follow_up_date?: string; follow_up_action?: string; status: FieldVisit["status"]; farmer_name?: string; farm_name?: string; plot_name?: string; plot_names?: string[]; assigned_staff_name?: string; plan_target_id?: string; visit_origin?: FieldVisit["visitOrigin"]; unscheduled_reason?: string; conducted_by_staff_id?: string; conducted_by_staff_name?: string; started_at?: string; completed_at?: string; check_in_latitude?: number; check_in_longitude?: number; check_in_gps_accuracy_metres?: number; check_out_latitude?: number; check_out_longitude?: number; check_out_gps_accuracy_metres?: number; reason_ids?: string[]; reason_names?: string[]; activity_count?: number; evidence_count?: number; follow_up_count?: number; }
export interface ApiVisitPlanTarget { id: string; plan_id: string; farmer_id: string; farm_id: string; farmer_name?: string; farm_name?: string; planned_date?: string; sequence_no?: number; status: VisitPlanTarget["status"]; actual_visit_id?: string; rescheduled_to_date?: string; missed_reason?: string; notes?: string; }
export interface ApiVisitPlan { id: string; plan_code: string; title: string; assigned_staff_id: string; assigned_staff_name?: string; planned_start_date: string; planned_end_date: string; status: VisitPlan["status"]; notes?: string; sanctioned_by_user_id?: string; sanctioned_at?: string; acknowledged_by_user_id?: string; acknowledged_at?: string; completed_at?: string; target_count: number; visited_count: number; missed_count: number; remaining_count: number; targets?: ApiVisitPlanTarget[]; }
export interface ApiVisitReason { id: string; reason_code: string; reason_name: string; description?: string; active: boolean; display_order: number; }
export interface ApiVisitActivityDefinition { id: string; activity_code: string; activity_name: string; category: string; description?: string; counts_towards_kpi: boolean; active: boolean; display_order: number; }
export interface ApiVisitActivity { id: string; visit_id: string; activity_id: string; plot_id?: string; distribution_id?: string; asset_id?: string; performed_at: string; notes?: string; quantity?: number; unit?: string; activity_name?: string; activity_code?: string; }
export interface ApiVisitPhotoEvidence { id: string; visit_id: string; photo_id: string; evidence_type: VisitPhotoEvidence["evidenceType"]; plot_id?: string; }
export interface ApiVisitFollowUp { id: string; visit_id: string; recommendation_id?: string; action: string; due_date: string; assigned_staff_id?: string; assigned_staff_name?: string; status: VisitFollowUp["status"]; completed_at?: string; completion_notes?: string; }
export interface ApiInput { id: string; code: string; name: string; category: string; unit: string; status: "ACTIVE" | "INACTIVE"; description?: string; stock_tracking_mode: "INVENTORY" | "NURSERY"; requires_mapped_farm: boolean; }
export interface ApiNurseryVariety { id: string; variety_code: string; variety_name: string; coffee_type: string; source_name?: string; source_region?: string; description?: string; active: boolean; }
export interface ApiNurserySite { id: string; nursery_code: string; nursery_name: string; geographic_unit_id?: string; responsible_employee_id?: string; description?: string; status: NurserySite["status"]; row_version: number; bed_count: number; current_seedlings: number; ready_seedlings: number; }
export interface ApiNurseryBed { id: string; nursery_id: string; bed_code: string; bed_name: string; estimated_capacity?: number; description?: string; status: NurseryBed["status"]; current_seedlings: number; ready_seedlings: number; lot_count: number; }
export interface ApiNurseryLotVariety { id: string; variety_id: string; variety_name?: string; variety_code?: string; estimated_quantity?: number; notes?: string; }
export interface ApiNurserySeedlingLot { id: string; lot_code: string; nursery_id: string; nursery_name?: string; bed_id: string; bed_name?: string; seedling_item_id: string; seedling_item_name?: string; source_name?: string; source_location?: string; propagation_started_date?: string; established_date?: string; initial_quantity: number; current_quantity: number; ready_quantity: number; distributed_quantity: number; variety_detail_status: NurserySeedlingLot["varietyDetailStatus"]; lifecycle_status: NurserySeedlingLot["lifecycleStatus"]; notes?: string; row_version: number; varieties?: ApiNurseryLotVariety[]; }
export interface ApiNurseryPopulationAssessment { id: string; lot_id: string; assessed_at: string; previous_quantity: number; current_viable_quantity: number; ready_quantity: number; mortality_since_last?: number; culled_since_last?: number; variety_detail_status: NurseryPopulationAssessment["varietyDetailStatus"]; notes?: string; assessed_by_employee_id?: string; }
export interface ApiNurseryActivityDefinition { id: string; activity_code: string; activity_name: string; category: string; description?: string; counts_towards_kpi: boolean; active: boolean; display_order: number; }
export interface ApiNurseryActivity { id: string; nursery_id: string; nursery_name?: string; bed_id?: string; bed_name?: string; lot_id?: string; lot_code?: string; activity_id: string; activity_code?: string; activity_name?: string; performed_at: string; notes?: string; responsible_employee_id?: string; responsible_employee_name?: string; input_count: number; }
export interface ApiNurseryStockSummary { lot_id: string; project_id: string; nursery_id: string; nursery_name: string; bed_id: string; bed_name: string; seedling_item_id: string; item_code: string; item_name: string; lot_code: string; initial_quantity: number; current_quantity: number; ready_quantity: number; distributed_quantity: number; variety_detail_status: NurseryStockSummary["varietyDetailStatus"]; lifecycle_status: NurseryStockSummary["lifecycleStatus"]; not_yet_ready_quantity: number; updated_at: string; }
export interface ApiSeedlingDistributionTarget { id: string; plan_id: string; farmer_id: string; farmer_name?: string; farm_id: string; farm_name?: string; plot_id: string; plot_name?: string; seedling_item_id: string; seedling_item_name?: string; planned_quantity: number; actual_received_quantity?: number; variety_detail_status: SeedlingDistributionTarget["varietyDetailStatus"]; status: SeedlingDistributionTarget["status"]; actual_distribution_id?: string; notes?: string; }
export interface ApiSeedlingDistributionPlan { id: string; plan_code: string; title: string; planned_start_date: string; planned_end_date: string; responsible_employee_id: string; responsible_employee_name?: string; status: SeedlingDistributionPlan["status"]; notes?: string; sanctioned_by_user_id?: string; sanctioned_at?: string; completed_at?: string; target_count: number; issued_count: number; remaining_count: number; total_planned_seedlings: number; total_received_seedlings: number; targets?: ApiSeedlingDistributionTarget[]; }
export interface ApiSeedlingDistributionSource { id: string; target_id: string; nursery_lot_id: string; lot_code?: string; nursery_name?: string; bed_name?: string; quantity: number; }
export interface ApiSeedlingDistributionVariety { id: string; target_id: string; variety_id: string; variety_code?: string; variety_name?: string; quantity?: number; notes?: string; }
export interface ApiInventoryLocation { id: string; location_code: string; location_name: string; status: string; }
export interface ApiInventoryBalance { item_id: string; item_code: string; item_name: string; location_id: string; location_name: string; quantity_on_hand: number; unit: string; as_of: string; }
export interface ApiStockMovement { id: string; movement_type: string; item_id: string; item_name?: string; inventory_location_id: string; location_name?: string; lot_id?: string; lot_number?: string; quantity: number; unit: string; occurred_at: string; notes?: string; reference?: string; }
export interface ApiDistribution { id: string; reference: string; farmer_id: string; farm_id?: string; plot_id?: string; item_id: string; quantity: number; unit: string; distribution_date: string; responsible_staff_id: string; status: InputDistribution["status"]; farmer_name?: string; farm_name?: string; plot_name?: string; item_name?: string; responsible_staff_name?: string; }
export interface ApiHarvest { id: string; reference: string; farmer_id: string; farm_id: string; delivery_date: string; quantity_kg: number; quality_grade: HarvestDelivery["qualityGrade"]; status: HarvestDelivery["status"]; payment_status: HarvestDelivery["paymentStatus"]; farmer_name?: string; farm_name?: string; }
export interface ApiDocument { id: string; entity_type: DocumentRecord["entityType"]; entity_id: string; document_type: string; bucket_id: DocumentRecord["bucketId"]; file_name: string; mime_type: string; size_bytes: number; confidential: boolean; access_state: DocumentRecord["accessState"]; uploaded_at: string; entity_label?: string; uploaded_by_name?: string; }
export interface ApiStaffAssignment { id: string; project_id: string; role_on_project: string; duty_station_geographic_unit_id?: string; supervisor_id?: string; start_date: string; end_date?: string; allocation_percentage?: number; status: "ACTIVE" | "INACTIVE" | "COMPLETED"; }
export interface ApiStaff { id: string; employee_code: string; first_name: string; middle_name?: string; last_name: string; sex?: string; phone?: string; email?: string; photo_url?: string; job_title: string; department: string; employment_type: string; employment_status: StaffDetail["employmentStatus"]; hire_date?: string; supervisor_id?: string; company_id?: string; created_at?: string; updated_at?: string; account_linked: boolean; assignments?: ApiStaffAssignment[]; project_id?: string; role_on_project?: string; duty_station_geographic_unit_id?: string; assignment_status?: "ACTIVE" | "INACTIVE" | "COMPLETED"; assignment_start_date?: string; assignment_end_date?: string; allocation_percentage?: number; project_assignment?: Record<string, unknown>; }

export const legacyToApiIds: Record<string, string> = {
  "farmer-001": "10000000-0000-4000-8000-000000000001",
  "farmer-002": "10000000-0000-4000-8000-000000000002",
  "farmer-003": "10000000-0000-4000-8000-000000000001",
  "farm-001": "20000000-0000-4000-8000-000000000001",
  "farm-002": "20000000-0000-4000-8000-000000000001",
  "farm-003": "20000000-0000-4000-8000-000000000002",
  "plot-001": "30000000-0000-4000-8000-000000000001",
  "plot-003": "30000000-0000-4000-8000-000000000001",
  "staff-002": "40000000-0000-4000-8000-000000000001",
  "staff-003": "40000000-0000-4000-8000-000000000001",
  "staff-004": "40000000-0000-4000-8000-000000000002",
  "input-001": "50000000-0000-4000-8000-000000000001",
  "input-002": "50000000-0000-4000-8000-000000000001",
};
export const toApiId = (id: string) => legacyToApiIds[id] || id;

const labelFor = (id: string | undefined, fallback: string) =>
  id ? `${fallback} ${id.slice(0, 8)}` : fallback;

const mapFarmerSex = (sex: string): FarmerDetail["gender"] => {
  if (sex === "MALE") return "Male";
  if (sex === "FEMALE") return "Female";
  if (sex === "OTHER") return "Other";
  return "Not stated";
};

const mapLocationPath = (label: string): FarmerDetail["location"] => {
  const parts = label.split("/").map((part) => part.trim()).filter(Boolean);
  const value = (index: number) => parts[index] || "Not recorded";
  const levelThree = value(2);
  const levelFour = value(3);
  const levelFive = value(4);
  return {
    district: value(0),
    county: value(1),
    levelThree,
    levelThreeType: /municipal division/i.test(levelThree) ? "Municipal Division" : /town council/i.test(levelThree) ? "Town Council" : "Subcounty",
    levelFour,
    levelFourType: /ward/i.test(levelFour) ? "Ward" : "Parish",
    levelFive,
    levelFiveType: /cell/i.test(levelFive) ? "Cell" : "Village",
  };
};

export const mapFarmer = (record: ApiFarmer): FarmerDetail => ({
  id: record.id,
  code: record.farmer_code,
  firstName: record.first_name,
  middleName: record.middle_name,
  sex: record.sex,
  lastName: record.last_name,
  initials: `${record.first_name.charAt(0)}${record.last_name.charAt(0)}`.toUpperCase(),
  gender: mapFarmerSex(record.sex),
  birthYearEstimated: record.birth_year_estimated,
  phoneMasked: record.primary_phone_last4 ? `•••• ${record.primary_phone_last4}` : "Not recorded",
  group: record.farmer_group_name || "Not assigned",
  location: mapLocationPath(record.geographic_unit_label),
  farms: record.initial_farm_id ? 1 : 0,
  acreage: 0,
  status: record.record_status === "APPROVED" ? "Verified" : record.record_status === "DRAFT" ? "Draft" : "Pending review",
  risk: "Low",
  registeredAt: record.created_at ? new Date(record.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "Not recorded",
  fieldOfficer: record.assigned_field_officer || "Unassigned",
  nationality: record.nationality,
  maritalStatus: record.marital_status,
  educationLevel: record.education_level,
  primaryOccupation: record.primary_occupation,
  farmerStatus: record.farmer_status,
  recordStatus: record.record_status,
  projectName: "ARISE Project",
  companyName: "ACZON Farms Ltd",
  assignedFieldOfficer: record.assigned_field_officer,
  assignedAgronomist: record.assigned_agronomist,
  geographicUnitId: record.geographic_unit_id,
  farmerGroupId: record.farmer_group_id,
  primaryPhoneLast4: record.primary_phone_last4,
  nationalIdLast4: record.national_id_last4,
  pendingAgreementRequirements: record.pending_agreement_requirements || 0,
  consentObtained: record.consent_obtained,
  rowVersion: record.row_version,
  household: {
    declaredHouseholdSize: record.household_size,
    declaredDependants: record.household_dependants,
    dependantsInHousehold: record.dependants_in_household,
    dependantsElsewhere: record.dependants_elsewhere,
    dependantsUnder18: record.dependants_under_18,
    notes: record.household_notes,
    members: [],
  },
  primaryNextOfKin: record.primary_next_of_kin_id && record.primary_next_of_kin_first_name && record.primary_next_of_kin_last_name && record.primary_next_of_kin_relationship_type ? {
    id: record.primary_next_of_kin_id,
    firstName: record.primary_next_of_kin_first_name,
    middleName: record.primary_next_of_kin_middle_name,
    lastName: record.primary_next_of_kin_last_name,
    relationshipType: record.primary_next_of_kin_relationship_type,
    livesInHousehold: record.primary_next_of_kin_lives_in_household ?? true,
    notes: record.primary_next_of_kin_notes,
    phoneLast4: record.primary_next_of_kin_phone_last4,
  } : undefined,
});

export const mapFarm = (record: ApiFarm, plots: ApiPlot[] = []): FarmDetail => {
  const activePlots = plots.filter((plot) => plot.status === "ACTIVE");
  const derivedArea = activePlots.reduce((sum, plot) => sum + (plot.reported_area_acres || 0), 0);
  const landUses = Array.from(new Set(activePlots.map((plot) => plot.current_land_use).filter(Boolean)));
  const allBoundariesVerified = activePlots.length > 0 && activePlots.every((plot) => plot.boundary_verified);
  return {
    id: record.id,
    code: record.farm_code,
    name: record.farm_name,
    farmerId: record.farmer_id,
    farmerName: record.farmer_name,
    farmerCode: record.farmer_id,
    district: record.geographic_unit_label.split("/")[0]?.trim() || "Not recorded",
    acreage: derivedArea,
    plots: activePlots.length,
    crop: landUses.length ? landUses.join(", ") : "Not recorded",
    boundaryStatus: allBoundariesVerified ? "Captured" : "Needs review",
    lastVisit: "Not recorded",
    projectName: "ARISE Project",
    locationPath: record.geographic_unit_label,
    reportedAreaAcres: derivedArea || undefined,
    gpsAreaAcres: record.gps_area_acres,
    acreageWarning: false,
    boundaryVerified: allBoundariesVerified,
    seedlingEligible: record.seedling_eligible,
    farmStatus: record.farm_status,
    recordStatus: record.record_status,
    farmerRecordStatus: record.farmer_record_status,
    plotsData: plots.map((plot) => ({
      id: plot.id,
      code: plot.plot_code,
      farmId: record.id,
      name: plot.plot_name,
      reportedAreaAcres: plot.reported_area_acres,
      gpsAreaAcres: plot.gps_area_acres,
      ownershipType: plot.ownership_type,
      currentLandUse: plot.current_land_use,
      localityName: plot.locality_name,
      ariseContractStartDate: plot.arise_contract_start_date,
      ariseContractEndDate: plot.arise_contract_end_date,
      ariseContractStatus: plot.arise_contract_status,
      centreLatitude: plot.centre_latitude,
      centreLongitude: plot.centre_longitude,
      centreAccuracyMetres: plot.centre_accuracy_metres,
      status: plot.status,
      recordStatus: plot.record_status,
      boundaryCaptured: plot.boundary_captured,
      boundaryVerified: plot.boundary_verified,
      mappingSessionId: plot.mapping_session_id,
      mappingStatus: plot.mapping_status,
      mappingVerifiedAt: plot.mapping_verified_at,
      rowVersion: plot.row_version,
    })),
    rowVersion: record.row_version,
  };
};

export const mapVisit = (record: ApiVisit): FieldVisit => ({
  id: record.id,
  reference: record.reference,
  visitDate: record.visit_date,
  farmerId: record.farmer_id,
  farmerName: record.farmer_name ?? labelFor(record.farmer_id, "Farmer"),
  farmId: record.farm_id,
  farmName: record.farm_name ?? labelFor(record.farm_id, "Farm"),
  plotId: record.plot_id,
  plotName: record.plot_id ? (record.plot_name ?? labelFor(record.plot_id, "Plot")) : undefined,
  plotIds: record.plot_ids,
  plotNames: record.plot_names,
  assignedStaffId: record.assigned_staff_id,
  assignedStaffName: record.assigned_staff_name ?? labelFor(record.assigned_staff_id, "Staff"),
  status: record.status,
  observations: record.observations,
  recommendations: record.recommendations,
  pestDiseaseObservation: record.pest_disease_observation,
  followUpDate: record.follow_up_date,
  followUpAction: record.follow_up_action,
  planTargetId: record.plan_target_id,
  visitOrigin: record.visit_origin,
  unscheduledReason: record.unscheduled_reason,
  conductedByStaffId: record.conducted_by_staff_id,
  conductedByStaffName: record.conducted_by_staff_name,
  startedAt: record.started_at,
  completedAt: record.completed_at,
  checkInLatitude: record.check_in_latitude,
  checkInLongitude: record.check_in_longitude,
  checkInGpsAccuracyMetres: record.check_in_gps_accuracy_metres,
  checkOutLatitude: record.check_out_latitude,
  checkOutLongitude: record.check_out_longitude,
  checkOutGpsAccuracyMetres: record.check_out_gps_accuracy_metres,
  reasonIds: record.reason_ids || [],
  reasonNames: record.reason_names || [],
  activityCount: record.activity_count || 0,
  evidenceCount: record.evidence_count || 0,
  followUpCount: record.follow_up_count || 0,
});

export const mapVisitPlanTarget = (record: ApiVisitPlanTarget): VisitPlanTarget => ({
  id: record.id,
  planId: record.plan_id,
  farmerId: record.farmer_id,
  farmId: record.farm_id,
  farmerName: record.farmer_name,
  farmName: record.farm_name,
  plannedDate: record.planned_date,
  sequenceNo: record.sequence_no,
  status: record.status,
  actualVisitId: record.actual_visit_id,
  rescheduledToDate: record.rescheduled_to_date,
  missedReason: record.missed_reason,
  notes: record.notes,
});

export const mapVisitPlan = (record: ApiVisitPlan): VisitPlan => ({
  id: record.id,
  planCode: record.plan_code,
  title: record.title,
  assignedStaffId: record.assigned_staff_id,
  assignedStaffName: record.assigned_staff_name,
  plannedStartDate: record.planned_start_date,
  plannedEndDate: record.planned_end_date,
  status: record.status,
  notes: record.notes,
  sanctionedByUserId: record.sanctioned_by_user_id,
  sanctionedAt: record.sanctioned_at,
  acknowledgedByUserId: record.acknowledged_by_user_id,
  acknowledgedAt: record.acknowledged_at,
  completedAt: record.completed_at,
  targetCount: record.target_count || 0,
  visitedCount: record.visited_count || 0,
  missedCount: record.missed_count || 0,
  remainingCount: record.remaining_count || 0,
  targets: (record.targets || []).map(mapVisitPlanTarget),
});

export const mapVisitReason = (record: ApiVisitReason): VisitReason => ({
  id: record.id, reasonCode: record.reason_code, reasonName: record.reason_name,
  description: record.description, active: record.active, displayOrder: record.display_order,
});

export const mapVisitActivityDefinition = (record: ApiVisitActivityDefinition): VisitActivityDefinition => ({
  id: record.id, activityCode: record.activity_code, activityName: record.activity_name,
  category: record.category, description: record.description,
  countsTowardsKpi: record.counts_towards_kpi, active: record.active, displayOrder: record.display_order,
});

export const mapVisitActivity = (record: ApiVisitActivity): VisitActivityRecord => ({
  id: record.id, visitId: record.visit_id, activityId: record.activity_id,
  activityCode: record.activity_code, activityName: record.activity_name, plotId: record.plot_id,
  distributionId: record.distribution_id, assetId: record.asset_id,
  performedAt: record.performed_at, notes: record.notes, quantity: record.quantity, unit: record.unit,
});

export const mapVisitEvidence = (record: ApiVisitPhotoEvidence): VisitPhotoEvidence => ({
  id: record.id, visitId: record.visit_id, photoId: record.photo_id,
  evidenceType: record.evidence_type, plotId: record.plot_id,
});

export const mapVisitFollowUp = (record: ApiVisitFollowUp): VisitFollowUp => ({
  id: record.id, visitId: record.visit_id, recommendationId: record.recommendation_id,
  action: record.action, dueDate: record.due_date, assignedStaffId: record.assigned_staff_id,
  assignedStaffName: record.assigned_staff_name, status: record.status,
  completedAt: record.completed_at, completionNotes: record.completion_notes,
});
export const mapInput = (record: ApiInput): InputCatalogueItem => ({
  id: record.id,
  code: record.code,
  name: record.name,
  category: record.category,
  unit: record.unit,
  status: record.status,
  description: record.description,
  stockTrackingMode: record.stock_tracking_mode,
  requiresMappedFarm: record.requires_mapped_farm,
  inventoryNote: record.stock_tracking_mode === "NURSERY"
    ? "Living seedling stock is managed in Nursery Management."
    : "Warehouse stock is managed in Inventory.",
});

export const mapNurseryVariety = (record: ApiNurseryVariety): NurseryVariety => ({
  id: record.id, varietyCode: record.variety_code, varietyName: record.variety_name,
  coffeeType: record.coffee_type, sourceName: record.source_name, sourceRegion: record.source_region,
  description: record.description, active: record.active,
});

export const mapNurserySite = (record: ApiNurserySite): NurserySite => ({
  id: record.id, nurseryCode: record.nursery_code, nurseryName: record.nursery_name,
  geographicUnitId: record.geographic_unit_id, responsibleEmployeeId: record.responsible_employee_id,
  description: record.description, status: record.status, rowVersion: record.row_version,
  bedCount: record.bed_count, currentSeedlings: record.current_seedlings, readySeedlings: record.ready_seedlings,
});

export const mapNurseryBed = (record: ApiNurseryBed): NurseryBed => ({
  id: record.id, nurseryId: record.nursery_id, bedCode: record.bed_code, bedName: record.bed_name,
  estimatedCapacity: record.estimated_capacity, description: record.description, status: record.status,
  currentSeedlings: record.current_seedlings, readySeedlings: record.ready_seedlings, lotCount: record.lot_count,
});

export const mapNurserySeedlingLot = (record: ApiNurserySeedlingLot): NurserySeedlingLot => ({
  id: record.id, lotCode: record.lot_code, nurseryId: record.nursery_id, nurseryName: record.nursery_name,
  bedId: record.bed_id, bedName: record.bed_name, seedlingItemId: record.seedling_item_id,
  seedlingItemName: record.seedling_item_name, sourceName: record.source_name, sourceLocation: record.source_location,
  propagationStartedDate: record.propagation_started_date, establishedDate: record.established_date,
  initialQuantity: record.initial_quantity, currentQuantity: record.current_quantity,
  readyQuantity: record.ready_quantity, distributedQuantity: record.distributed_quantity,
  varietyDetailStatus: record.variety_detail_status, lifecycleStatus: record.lifecycle_status,
  notes: record.notes, rowVersion: record.row_version,
  varieties: (record.varieties || []).map((item) => ({
    id: item.id, varietyId: item.variety_id, varietyName: item.variety_name,
    varietyCode: item.variety_code, estimatedQuantity: item.estimated_quantity, notes: item.notes,
  })),
});

export const mapNurseryPopulationAssessment = (record: ApiNurseryPopulationAssessment): NurseryPopulationAssessment => ({
  id: record.id, lotId: record.lot_id, assessedAt: record.assessed_at,
  previousQuantity: record.previous_quantity, currentViableQuantity: record.current_viable_quantity,
  readyQuantity: record.ready_quantity, mortalitySinceLast: record.mortality_since_last,
  culledSinceLast: record.culled_since_last, varietyDetailStatus: record.variety_detail_status,
  notes: record.notes, assessedByEmployeeId: record.assessed_by_employee_id,
});

export const mapNurseryActivityDefinition = (record: ApiNurseryActivityDefinition): NurseryActivityDefinition => ({
  id: record.id, activityCode: record.activity_code, activityName: record.activity_name,
  category: record.category, description: record.description, countsTowardsKpi: record.counts_towards_kpi,
  active: record.active, displayOrder: record.display_order,
});

export const mapNurseryActivity = (record: ApiNurseryActivity): NurseryActivity => ({
  id: record.id, nurseryId: record.nursery_id, nurseryName: record.nursery_name,
  bedId: record.bed_id, bedName: record.bed_name, lotId: record.lot_id, lotCode: record.lot_code,
  activityId: record.activity_id, activityCode: record.activity_code, activityName: record.activity_name,
  performedAt: record.performed_at, notes: record.notes, responsibleEmployeeId: record.responsible_employee_id,
  responsibleEmployeeName: record.responsible_employee_name, inputCount: record.input_count,
});

export const mapNurseryStock = (record: ApiNurseryStockSummary): NurseryStockSummary => ({
  lotId: record.lot_id, projectId: record.project_id, nurseryId: record.nursery_id,
  nurseryName: record.nursery_name, bedId: record.bed_id, bedName: record.bed_name,
  seedlingItemId: record.seedling_item_id, itemCode: record.item_code, itemName: record.item_name,
  lotCode: record.lot_code, initialQuantity: record.initial_quantity, currentQuantity: record.current_quantity,
  readyQuantity: record.ready_quantity, distributedQuantity: record.distributed_quantity,
  varietyDetailStatus: record.variety_detail_status, lifecycleStatus: record.lifecycle_status,
  notYetReadyQuantity: record.not_yet_ready_quantity, updatedAt: record.updated_at,
});

export const mapSeedlingDistributionTarget = (record: ApiSeedlingDistributionTarget): SeedlingDistributionTarget => ({
  id: record.id, planId: record.plan_id, farmerId: record.farmer_id, farmerName: record.farmer_name,
  farmId: record.farm_id, farmName: record.farm_name, plotId: record.plot_id, plotName: record.plot_name,
  seedlingItemId: record.seedling_item_id, seedlingItemName: record.seedling_item_name,
  plannedQuantity: record.planned_quantity, actualReceivedQuantity: record.actual_received_quantity,
  varietyDetailStatus: record.variety_detail_status, status: record.status,
  actualDistributionId: record.actual_distribution_id, notes: record.notes,
});

export const mapSeedlingDistributionPlan = (record: ApiSeedlingDistributionPlan): SeedlingDistributionPlan => ({
  id: record.id, planCode: record.plan_code, title: record.title,
  plannedStartDate: record.planned_start_date, plannedEndDate: record.planned_end_date,
  responsibleEmployeeId: record.responsible_employee_id, responsibleEmployeeName: record.responsible_employee_name,
  status: record.status, notes: record.notes, sanctionedByUserId: record.sanctioned_by_user_id,
  sanctionedAt: record.sanctioned_at, completedAt: record.completed_at,
  targetCount: record.target_count, issuedCount: record.issued_count, remainingCount: record.remaining_count,
  totalPlannedSeedlings: record.total_planned_seedlings, totalReceivedSeedlings: record.total_received_seedlings,
  targets: (record.targets || []).map(mapSeedlingDistributionTarget),
});

export const mapSeedlingDistributionSource = (record: ApiSeedlingDistributionSource): SeedlingDistributionSource => ({
  id: record.id, targetId: record.target_id, nurseryLotId: record.nursery_lot_id,
  lotCode: record.lot_code, nurseryName: record.nursery_name, bedName: record.bed_name, quantity: record.quantity,
});

export const mapSeedlingDistributionVariety = (record: ApiSeedlingDistributionVariety): SeedlingDistributionVariety => ({
  id: record.id, targetId: record.target_id, varietyId: record.variety_id,
  varietyCode: record.variety_code, varietyName: record.variety_name, quantity: record.quantity, notes: record.notes,
});

export const mapInventoryLocation = (record: ApiInventoryLocation): InventoryLocation => ({ id: record.id, locationCode: record.location_code, locationName: record.location_name, status: record.status });
export const mapInventoryBalance = (record: ApiInventoryBalance): InventoryBalance => ({ itemId: record.item_id, itemCode: record.item_code, itemName: record.item_name, locationId: record.location_id, locationName: record.location_name, quantityOnHand: record.quantity_on_hand, unit: record.unit, asOf: record.as_of });
export const mapStockMovement = (record: ApiStockMovement): StockMovement => ({ id: record.id, movementType: record.movement_type, itemId: record.item_id, itemName: record.item_name, inventoryLocationId: record.inventory_location_id, locationName: record.location_name, quantity: record.quantity, unit: record.unit, occurredAt: record.occurred_at, notes: record.notes, reference: record.reference });
export const mapDistribution = (record: ApiDistribution): InputDistribution => ({ id: record.id, reference: record.reference, farmerId: record.farmer_id, farmerName: record.farmer_name ?? labelFor(record.farmer_id, "Farmer"), farmId: record.farm_id, farmName: record.farm_name ?? labelFor(record.farm_id, "Farm"), plotId: record.plot_id, plotName: record.plot_id ? (record.plot_name ?? labelFor(record.plot_id, "Plot")) : undefined, itemId: record.item_id, itemName: record.item_name ?? labelFor(record.item_id, "Input"), quantity: record.quantity, unit: record.unit, distributionDate: record.distribution_date, responsibleStaffId: record.responsible_staff_id, responsibleStaff: record.responsible_staff_name ?? labelFor(record.responsible_staff_id, "Staff"), status: record.status });
export const mapHarvest = (record: ApiHarvest): HarvestDelivery => ({ id: record.id, reference: record.reference, farmerId: record.farmer_id, farmerName: record.farmer_name ?? labelFor(record.farmer_id, "Farmer"), farmId: record.farm_id, farmName: record.farm_name ?? labelFor(record.farm_id, "Farm"), deliveryDate: record.delivery_date, quantityKg: record.quantity_kg, qualityGrade: record.quality_grade, status: record.status, paymentStatus: record.payment_status });
export const mapDocument = (record: ApiDocument): DocumentRecord => { const apiLabel = record.entity_label ?? (record.entity_type === "FARMER" ? labelFor(record.entity_id, "Farmer") : record.entity_type === "FARM" ? labelFor(record.entity_id, "Farm") : labelFor(record.entity_id, "Plot")); return { id: record.id, entityType: record.entity_type, entityId: record.entity_id, entityLabel: apiLabel, documentType: record.document_type, bucketId: record.bucket_id, fileName: record.file_name, mimeType: record.mime_type, sizeBytes: record.size_bytes, uploadedBy: record.uploaded_by_name ?? "Current user", uploadedAt: record.uploaded_at, confidential: record.confidential, accessState: record.access_state }; };
export const mapStaff = (record: ApiStaff): StaffDetail => {
  const detailAssignments: StaffDetail["assignments"] = (record.assignments || []).map((a) => ({
    id: a.id,
    projectId: a.project_id,
    roleOnProject: a.role_on_project,
    dutyStationGeographicUnitId: a.duty_station_geographic_unit_id,
    supervisorId: a.supervisor_id,
    startDate: a.start_date,
    endDate: a.end_date,
    allocationPercentage: a.allocation_percentage,
    status: a.status,
  }));
  const directoryAssignment: StaffDetail["assignments"][number] | undefined =
    record.project_id && record.role_on_project && record.assignment_status
      ? {
          id: `directory-${record.id}-${record.project_id}`,
          projectId: record.project_id,
          roleOnProject: record.role_on_project,
          dutyStationGeographicUnitId: record.duty_station_geographic_unit_id,
          supervisorId: record.supervisor_id,
          startDate: record.assignment_start_date || "",
          endDate: record.assignment_end_date,
          allocationPercentage: record.allocation_percentage,
          status: record.assignment_status,
        }
      : undefined;
  const assignments = detailAssignments.length > 0
    ? detailAssignments
    : (directoryAssignment ? [directoryAssignment] : []);
  const primaryAssignment = assignments.find((a) => a.status === "ACTIVE") || assignments[0];
  const fullName = `${record.first_name} ${record.last_name}`;
  const initials = [record.first_name, record.last_name].filter(Boolean).map((n) => n[0] || "").join("").toUpperCase() || "?";
  return {
    id: record.id,
    name: fullName,
    initials,
    role: record.job_title,
    territory: primaryAssignment?.dutyStationGeographicUnitId || "ARISE",
    status: record.employment_status === "ACTIVE" ? "Active" : "Invited",
    lastActive: record.updated_at || record.created_at || "",
    employeeCode: record.employee_code,
    firstName: record.first_name,
    middleName: record.middle_name,
    lastName: record.last_name,
    sex: record.sex as StaffDetail["sex"],
    phone: record.phone,
    email: record.email,
    jobTitle: record.job_title,
    department: record.department,
    employmentType: record.employment_type,
    hireDate: record.hire_date,
    employmentStatus: record.employment_status,
    supervisorId: record.supervisor_id,
    companyId: record.company_id,
    accountLinked: record.account_linked,
    assignments,
    projectAssignment: {
      projectName: "ARISE Project",
      roleOnProject: primaryAssignment?.roleOnProject || record.job_title,
      dutyStation: primaryAssignment?.dutyStationGeographicUnitId || "ARISE",
      allocationPercentage: primaryAssignment?.allocationPercentage ?? 100,
      status: primaryAssignment?.status || "ACTIVE",
    },
    permissions: [],
  };
};

export interface ApiAsset {
  id: string; company_id?: string; project_id?: string;
  asset_code: string; asset_name: string; asset_category: string;
  serial_number?: string; acquisition_date?: string; acquisition_cost?: number;
  currency?: string; current_geographic_unit_id?: string; current_geographic_unit_label?: string;
  status: string; retired_at?: string; retirement_reason?: string;
  current_assignment?: ApiAssetAssignment; row_version: number; updated_at: string;
}
export interface ApiAssetAssignment {
  id: string; asset_id: string; project_id: string;
  employee_id?: string; employee_name?: string; geographic_unit_id?: string;
  assigned_at: string; expected_return_at?: string; returned_at?: string;
  assigned_by: string; received_condition?: string; returned_condition?: string;
  status: string; created_at: string;
}
export interface ApiAssetMaintenance {
  id: string; asset_id: string; project_id: string;
  maintenance_date: string; maintenance_type: string; description?: string;
  outcome?: string; performed_by?: string; next_service_date?: string;
  cost?: number; status: string; created_at: string;
}
export const mapAsset = (r: ApiAsset): Asset => ({
  id: r.id, companyId: r.company_id, projectId: r.project_id,
  assetCode: r.asset_code, assetName: r.asset_name, assetCategory: r.asset_category,
  serialNumber: r.serial_number, acquisitionDate: r.acquisition_date,
  acquisitionCost: r.acquisition_cost, currency: r.currency,
  currentGeographicUnitId: r.current_geographic_unit_id,
  currentGeographicUnitLabel: r.current_geographic_unit_label,
  status: r.status as Asset["status"], retiredAt: r.retired_at,
  retirementReason: r.retirement_reason,
  currentAssignment: r.current_assignment ? mapAssetAssignment(r.current_assignment) : undefined,
  rowVersion: r.row_version, updatedAt: r.updated_at,
});
export const mapAssetAssignment = (r: ApiAssetAssignment): AssetAssignment => ({
  id: r.id, assetId: r.asset_id, projectId: r.project_id,
  employeeId: r.employee_id, employeeName: r.employee_name,
  geographicUnitId: r.geographic_unit_id, assignedAt: r.assigned_at,
  expectedReturnAt: r.expected_return_at, returnedAt: r.returned_at,
  assignedBy: r.assigned_by, receivedCondition: r.received_condition,
  returnedCondition: r.returned_condition, status: r.status as AssetAssignment["status"],
  createdAt: r.created_at,
});
export const mapAssetMaintenance = (r: ApiAssetMaintenance): AssetMaintenance => ({
  id: r.id, assetId: r.asset_id, projectId: r.project_id,
  maintenanceDate: r.maintenance_date, maintenanceType: r.maintenance_type,
  description: r.description, outcome: r.outcome, performedBy: r.performed_by,
  nextServiceDate: r.next_service_date, cost: r.cost,
  status: r.status as AssetMaintenance["status"], createdAt: r.created_at,
});

export interface ApiFiscalPeriod {
  id: string; company_id: string; period_code: string; period_name: string;
  start_date: string; end_date: string; status: string; row_version: number;
}
export const mapFiscalPeriod = (r: ApiFiscalPeriod): FiscalPeriod => ({
  id: r.id, companyId: r.company_id, periodCode: r.period_code,
  periodName: r.period_name, startDate: r.start_date, endDate: r.end_date,
  status: r.status as FiscalPeriod["status"], rowVersion: r.row_version,
});

export interface ApiLedgerAccount {
  id: string; company_id: string; account_code: string; account_name: string;
  account_type: string; parent_account_id?: string; currency?: string;
  active: boolean; row_version: number;
}
export const mapLedgerAccount = (r: ApiLedgerAccount): LedgerAccount => ({
  id: r.id, companyId: r.company_id, accountCode: r.account_code,
  accountName: r.account_name, accountType: r.account_type as LedgerAccount["accountType"],
  parentAccountId: r.parent_account_id, currency: r.currency,
  active: r.active, rowVersion: r.row_version,
});

export interface ApiJournalLine {
  id: string; journal_entry_id: string; line_number: number;
  account_id: string; account_code?: string; account_name?: string;
  farmer_id?: string; employee_id?: string; description?: string;
  debit_amount: number; credit_amount: number; currency: string;
}
export const mapJournalLine = (r: ApiJournalLine): JournalLine => ({
  id: r.id, journalEntryId: r.journal_entry_id, lineNumber: r.line_number,
  accountId: r.account_id, accountCode: r.account_code, accountName: r.account_name,
  farmerId: r.farmer_id, employeeId: r.employee_id, description: r.description,
  debitAmount: r.debit_amount, creditAmount: r.credit_amount, currency: r.currency,
});

export interface ApiJournalEntry {
  id: string; company_id: string; project_id?: string; fiscal_period_id: string;
  journal_reference: string; entry_date: string; description: string;
  source_entity_type?: string; source_entity_id?: string; status: string;
  approved_by?: string; approved_at?: string; posted_by?: string; posted_at?: string;
  reversal_of_id?: string; idempotency_key: string; row_version: number;
  lines?: ApiJournalLine[]; debit_total: number; credit_total: number;
}
export const mapJournalEntry = (r: ApiJournalEntry): JournalEntry => ({
  id: r.id, companyId: r.company_id, projectId: r.project_id,
  fiscalPeriodId: r.fiscal_period_id, journalReference: r.journal_reference,
  entryDate: r.entry_date, description: r.description,
  sourceEntityType: r.source_entity_type, sourceEntityId: r.source_entity_id,
  status: r.status as JournalEntry["status"], approvedBy: r.approved_by,
  approvedAt: r.approved_at, postedBy: r.posted_by, postedAt: r.posted_at,
  reversalOfId: r.reversal_of_id, idempotencyKey: r.idempotency_key,
  rowVersion: r.row_version,
  lines: (r.lines || []).map(mapJournalLine),
  debitTotal: r.debit_total, creditTotal: r.credit_total,
});

export const mapPaymentRequestKind = (kind?: string | null): PaymentRequest["requestKind"] => {
  if (kind === "PROGRAMME_EXPENSE" || kind === "FARMER_PAYMENT" || kind === "FARMER_ADVANCE" || kind === "OTHER") return kind;
  return undefined;
};

export interface ApiPaymentRequest {
  id: string; company_id: string; project_id?: string; request_reference: string;
  request_kind?: string | null;
  beneficiary_type: string; beneficiary_id?: string; amount: number; currency: string;
  purpose: string; status: string; requested_by: string;
  submitted_by?: string | null; submitted_at?: string | null;
  approved_by?: string; approved_at?: string;
  rejected_by?: string | null; rejected_at?: string | null; rejection_reason?: string | null;
  cancelled_by?: string | null; cancelled_at?: string | null; cancellation_reason?: string | null;
  expense_category?: string | null; expense_date?: string | null;
  source_module?: string | null; source_entity_type?: string | null; source_entity_id?: string | null;
  support_document_reference?: string | null; ledger_account_id?: string | null; geographic_unit_id?: string | null;
  notes?: string | null; payment_method?: string | null; external_payment_reference?: string | null;
  paid_at?: string; idempotency_key: string; row_version: number;
  created_at: string;
}
export const mapPaymentRequest = (r: ApiPaymentRequest): PaymentRequest => ({
  id: r.id, companyId: r.company_id, projectId: r.project_id,
  requestReference: r.request_reference,
  requestKind: mapPaymentRequestKind(r.request_kind),
  beneficiaryType: r.beneficiary_type,
  beneficiaryId: r.beneficiary_id, amount: r.amount, currency: r.currency,
  purpose: r.purpose, status: r.status as PaymentRequest["status"],
  requestedBy: r.requested_by, submittedBy: r.submitted_by ?? undefined, submittedAt: r.submitted_at ?? undefined,
  approvedBy: r.approved_by, approvedAt: r.approved_at,
  rejectedBy: r.rejected_by ?? undefined, rejectedAt: r.rejected_at ?? undefined, rejectionReason: r.rejection_reason ?? undefined,
  cancelledBy: r.cancelled_by ?? undefined, cancelledAt: r.cancelled_at ?? undefined, cancellationReason: r.cancellation_reason ?? undefined,
  expenseCategory: r.expense_category ?? undefined, expenseDate: r.expense_date ?? undefined,
  sourceModule: r.source_module ?? undefined, sourceEntityType: r.source_entity_type ?? undefined, sourceEntityId: r.source_entity_id ?? undefined,
  supportDocumentReference: r.support_document_reference ?? undefined,
  ledgerAccountId: r.ledger_account_id ?? undefined, geographicUnitId: r.geographic_unit_id ?? undefined,
  notes: r.notes ?? undefined,
  paymentMethod: r.payment_method ?? undefined, externalPaymentReference: r.external_payment_reference ?? undefined,
  paidAt: r.paid_at, idempotencyKey: r.idempotency_key, rowVersion: r.row_version,
  createdAt: r.created_at,
});

// ── Phase 5E: Commercial chain mappers ────────────────────────

export interface ApiBuyingPrice {
  id: string; project_id: string; crop: string; product_form: string; quality_grade?: string;
  currency: string; unit: string; unit_price: number; effective_from: string; effective_until?: string;
  status: string; notes?: string;
}
export const mapBuyingPrice = (r: ApiBuyingPrice): BuyingPrice => ({
  id: r.id, projectId: r.project_id, crop: r.crop, productForm: r.product_form,
  qualityGrade: r.quality_grade, currency: r.currency, unit: r.unit, unitPrice: r.unit_price,
  effectiveFrom: r.effective_from, effectiveUntil: r.effective_until, status: r.status, notes: r.notes,
});

export interface ApiHarvestSeason {
  id: string; project_id: string; season_code: string; season_name: string;
  calendar_year: number; season_number: number; start_date: string; end_date: string;
  delivery_deadline: string; status: HarvestSeason["status"]; notes?: string;
  created_at: string; updated_at: string; row_version: number;
}
export const mapHarvestSeason = (r: ApiHarvestSeason): HarvestSeason => ({
  id: r.id, projectId: r.project_id, seasonCode: r.season_code, seasonName: r.season_name,
  calendarYear: r.calendar_year, seasonNumber: r.season_number, startDate: r.start_date,
  endDate: r.end_date, deliveryDeadline: r.delivery_deadline, status: r.status,
  notes: r.notes, rowVersion: r.row_version,
});

export interface ApiFarmerHarvestSeasonSummary {
  project_id: string; harvest_season_id: string; season_code: string; season_name: string;
  calendar_year: number; season_number: number; farmer_id: string; farmer_name: string;
  farm_id: string; farm_name: string; district_name?: string; delivery_count: number;
  gross_quantity_kg: number; verified_accepted_kg: number; rejected_quantity_kg: number;
  farmer_share_quantity_kg: number; company_share_quantity_kg: number;
  first_delivery_at?: string; latest_delivery_at?: string;
}
export const mapFarmerHarvestSeasonSummary = (r: ApiFarmerHarvestSeasonSummary): FarmerHarvestSeasonSummary => ({
  projectId: r.project_id, harvestSeasonId: r.harvest_season_id, seasonCode: r.season_code,
  seasonName: r.season_name, calendarYear: r.calendar_year, seasonNumber: r.season_number,
  farmerId: r.farmer_id, farmerName: r.farmer_name, farmId: r.farm_id, farmName: r.farm_name,
  districtName: r.district_name, deliveryCount: Number(r.delivery_count || 0),
  grossQuantityKg: Number(r.gross_quantity_kg || 0), verifiedAcceptedKg: Number(r.verified_accepted_kg || 0),
  rejectedQuantityKg: Number(r.rejected_quantity_kg || 0),
  farmerShareQuantityKg: Number(r.farmer_share_quantity_kg || 0),
  companyShareQuantityKg: Number(r.company_share_quantity_kg || 0),
  firstDeliveryAt: r.first_delivery_at, latestDeliveryAt: r.latest_delivery_at,
});

export interface ApiHarvestRegionSeasonSummary {
  project_id: string; harvest_season_id: string; season_code: string; season_name: string;
  calendar_year: number; season_number: number; district_name: string; farmers_delivering: number;
  delivery_count: number; gross_quantity_kg: number; verified_accepted_kg: number;
  farmer_share_quantity_kg: number; company_share_quantity_kg: number;
}
export const mapHarvestRegionSeasonSummary = (r: ApiHarvestRegionSeasonSummary): HarvestRegionSeasonSummary => ({
  projectId: r.project_id, harvestSeasonId: r.harvest_season_id, seasonCode: r.season_code,
  seasonName: r.season_name, calendarYear: r.calendar_year, seasonNumber: r.season_number,
  districtName: r.district_name, farmersDelivering: Number(r.farmers_delivering || 0),
  deliveryCount: Number(r.delivery_count || 0), grossQuantityKg: Number(r.gross_quantity_kg || 0),
  verifiedAcceptedKg: Number(r.verified_accepted_kg || 0),
  farmerShareQuantityKg: Number(r.farmer_share_quantity_kg || 0),
  companyShareQuantityKg: Number(r.company_share_quantity_kg || 0),
});

export interface ApiHarvestPlotContext {
  plot_id: string; farm_id: string; farmer_id: string; project_id: string; plot_name: string;
  plot_status: string; reported_area_acres?: number; gps_area_acres?: number;
  effective_date?: string; expiry_date?: string; programme_term_status?: string;
  current_programme_year?: number; approximate_years_remaining?: number;
  current_year_expected_yield_kg?: number; next_year_expected_yield_kg?: number;
}
export const mapHarvestPlotContext = (r: ApiHarvestPlotContext): HarvestPlotContext => ({
  plotId: r.plot_id, farmId: r.farm_id, farmerId: r.farmer_id, projectId: r.project_id,
  plotName: r.plot_name, plotStatus: r.plot_status, reportedAreaAcres: r.reported_area_acres,
  gpsAreaAcres: r.gps_area_acres, effectiveDate: r.effective_date, expiryDate: r.expiry_date,
  programmeTermStatus: r.programme_term_status, currentProgrammeYear: r.current_programme_year,
  approximateYearsRemaining: r.approximate_years_remaining,
  currentYearExpectedYieldKg: r.current_year_expected_yield_kg,
  nextYearExpectedYieldKg: r.next_year_expected_yield_kg,
});

export interface ApiDelivery {
  id: string; project_id: string; delivery_reference: string; farmer_id: string;
  farm_id?: string; farm_name?: string; plot_id?: string; harvest_season_id?: string;
  season_code?: string; season_name?: string; district_name?: string;
  aggregation_geographic_unit_id?: string; received_by_employee_id?: string;
  delivered_at: string; gross_quantity: number; accepted_quantity?: number;
  rejected_quantity?: number; unit: string; quality_grade?: string; moisture_pct?: number;
  product_form: string; status: string; rejection_reason?: string; row_version: number;
  farmer_name?: string; farmer_share_pct?: number; company_share_pct?: number;
  farmer_share_quantity_kg?: number; company_share_quantity_kg?: number; rule_version?: string;
}
export const mapDelivery = (r: ApiDelivery): CommercialDelivery => ({
  id: r.id, projectId: r.project_id, deliveryReference: r.delivery_reference,
  farmerId: r.farmer_id, farmId: r.farm_id, farmName: r.farm_name,
  farmerName: r.farmer_name || "", harvestSeasonId: r.harvest_season_id,
  seasonCode: r.season_code, seasonName: r.season_name, districtName: r.district_name,
  deliveredAt: r.delivered_at, grossQuantity: Number(r.gross_quantity),
  acceptedQuantity: r.accepted_quantity === undefined ? undefined : Number(r.accepted_quantity),
  rejectedQuantity: r.rejected_quantity === undefined ? undefined : Number(r.rejected_quantity),
  unit: r.unit, qualityGrade: r.quality_grade, moisturePct: r.moisture_pct,
  productForm: r.product_form, status: r.status,
  farmerSharePct: r.farmer_share_pct === undefined ? undefined : Number(r.farmer_share_pct),
  companySharePct: r.company_share_pct === undefined ? undefined : Number(r.company_share_pct),
  farmerShareQuantityKg: r.farmer_share_quantity_kg === undefined ? undefined : Number(r.farmer_share_quantity_kg),
  companyShareQuantityKg: r.company_share_quantity_kg === undefined ? undefined : Number(r.company_share_quantity_kg),
  ruleVersion: r.rule_version,
});

export interface ApiCommercialTxn {
  id: string; project_id: string; farmer_id: string; delivery_id?: string;
  buying_price_id?: string; applied_unit_price?: number;
  transaction_reference: string; transaction_date: string; currency: string;
  gross_amount?: number; company_share_amount?: number; farmer_payable_amount?: number;
  calculation_basis?: Record<string, unknown>; calculation_rule_version?: string;
  status: string; approved_by?: string; approved_at?: string; posted_at?: string;
  row_version: number; farmer_name?: string; delivery_reference?: string;
}
export const mapCommercialTxn = (r: ApiCommercialTxn): CommercialTransaction => ({
  id: r.id, projectId: r.project_id, farmerId: r.farmer_id, deliveryId: r.delivery_id,
  buyingPriceId: r.buying_price_id, appliedUnitPrice: r.applied_unit_price,
  transactionReference: r.transaction_reference, transactionDate: r.transaction_date,
  currency: r.currency, grossAmount: r.gross_amount, companyShareAmount: r.company_share_amount,
  farmerPayableAmount: r.farmer_payable_amount, calculationBasis: r.calculation_basis,
  calculationRuleVersion: r.calculation_rule_version, status: r.status,
  farmerName: r.farmer_name, deliveryReference: r.delivery_reference,
});

export interface ApiFarmerPayment {
  id: string; project_id: string; farmer_id: string; payment_reference: string;
  currency: string; amount: number; payment_method?: string;
  external_provider_reference?: string; finance_payment_request_id?: string;
  status: string; requested_at: string; approved_by?: string; approved_at?: string;
  paid_at?: string; failure_reason?: string; idempotency_key: string; row_version: number;
  farmer_name?: string; allocated_transaction_ids?: string[];
}
export const mapFarmerPayment = (r: ApiFarmerPayment): FarmerPayment => ({
  id: r.id, projectId: r.project_id, farmerId: r.farmer_id,
  paymentReference: r.payment_reference, currency: r.currency, amount: r.amount,
  paymentMethod: r.payment_method, financePaymentRequestId: r.finance_payment_request_id,
  status: r.status, requestedAt: r.requested_at, approvedAt: r.approved_at,
  paidAt: r.paid_at, farmerName: r.farmer_name,
  allocatedTransactionIds: r.allocated_transaction_ids || [],
});

export interface ApiFarmerBalance {
  farmer_id: string; farmer_name?: string; posted_transaction_count: number;
  total_due: number; total_paid: number; total_outstanding: number;
  total_advance_recovered?: number;
}
export const mapFarmerBalance = (r: ApiFarmerBalance): FarmerBalance => ({
  farmerId: r.farmer_id, farmerName: r.farmer_name,
  postedTransactionCount: r.posted_transaction_count,
  totalDue: r.total_due, totalPaid: r.total_paid, totalOutstanding: r.total_outstanding,
  totalAdvanceRecovered: Number(r.total_advance_recovered ?? 0),
});

// ── Farmer Advances mappers ───────────────────────────────────

export interface ApiFarmerAdvance {
  id: string; project_id: string; farmer_id: string; farmer_name?: string;
  farm_id?: string; farm_name?: string; harvest_season_id?: string;
  season_code?: string; season_name?: string;
  advance_reference: string; currency: string; principal_amount: number;
  purpose: string; notes?: string; status: FarmerAdvance["status"];
  finance_payment_request_id?: string;
  requested_by?: string; requested_at?: string;
  approved_by?: string; approved_at?: string;
  cancelled_by?: string; cancelled_at?: string; cancellation_reason?: string;
  disbursed_by?: string; disbursed_at?: string;
  payment_method?: string; external_payment_reference?: string;
  created_at: string; updated_at: string; row_version: number;
  gross_recovered_amount: number; reversed_recovery_amount: number;
  net_recovered_amount: number; outstanding_amount: number;
  derived_balance_status?: string;
}
export const mapFarmerAdvance = (r: ApiFarmerAdvance): FarmerAdvance => ({
  id: r.id, projectId: r.project_id, farmerId: r.farmer_id, farmerName: r.farmer_name,
  farmId: r.farm_id, farmName: r.farm_name, harvestSeasonId: r.harvest_season_id,
  seasonCode: r.season_code, seasonName: r.season_name,
  advanceReference: r.advance_reference, currency: r.currency,
  principalAmount: Number(r.principal_amount), purpose: r.purpose, notes: r.notes,
  status: r.status, financePaymentRequestId: r.finance_payment_request_id,
  requestedBy: r.requested_by, requestedAt: r.requested_at,
  approvedBy: r.approved_by, approvedAt: r.approved_at,
  cancelledBy: r.cancelled_by, cancelledAt: r.cancelled_at,
  cancellationReason: r.cancellation_reason,
  disbursedBy: r.disbursed_by, disbursedAt: r.disbursed_at,
  paymentMethod: r.payment_method, externalPaymentReference: r.external_payment_reference,
  createdAt: r.created_at, updatedAt: r.updated_at, rowVersion: r.row_version,
  grossRecoveredAmount: Number(r.gross_recovered_amount ?? 0),
  reversedRecoveryAmount: Number(r.reversed_recovery_amount ?? 0),
  netRecoveredAmount: Number(r.net_recovered_amount ?? 0),
  outstandingAmount: Number(r.outstanding_amount ?? 0),
  derivedBalanceStatus: r.derived_balance_status,
});

export interface ApiFarmerAdvanceRecovery {
  id: string; project_id: string; advance_id: string; farmer_id: string;
  settlement_id?: string; recovery_reference: string; recovery_date: string;
  amount: number; currency: string;
  recovery_source: FarmerAdvanceRecovery["recoverySource"];
  entry_type: FarmerAdvanceRecovery["entryType"];
  reversal_of_id?: string; notes?: string;
  created_by_user_id: string; created_at: string;
}
export const mapFarmerAdvanceRecovery = (r: ApiFarmerAdvanceRecovery): FarmerAdvanceRecovery => ({
  id: r.id, projectId: r.project_id, advanceId: r.advance_id, farmerId: r.farmer_id,
  settlementId: r.settlement_id, recoveryReference: r.recovery_reference,
  recoveryDate: r.recovery_date, amount: Number(r.amount), currency: r.currency,
  recoverySource: r.recovery_source, entryType: r.entry_type,
  reversalOfId: r.reversal_of_id, notes: r.notes,
  createdByUserId: r.created_by_user_id, createdAt: r.created_at,
});

// ── Phase 5F: Agreements & Records mappers ────────────────────

export interface ApiAgreement {
  id: string; project_id: string; farmer_id: string;
  agreement_type: string; agreement_reference: string;
  farmer_share_pct: number; company_share_pct: number;
  signed_date?: string; effective_date: string; expiry_date: string;
  status: string; document_object_path?: string;
  responsible_staff_id?: string;
  captured_at: string; synced_at?: string;
  created_at: string; updated_at: string; row_version: number;
  planning_only: boolean; farmer_name?: string;
}
export const mapAgreement = (r: ApiAgreement): FarmerAgreement => ({
  id: r.id, projectId: r.project_id, farmerId: r.farmer_id,
  agreementType: r.agreement_type as FarmerAgreement["agreementType"],
  agreementReference: r.agreement_reference,
  farmerSharePct: r.farmer_share_pct, companySharePct: r.company_share_pct,
  signedDate: r.signed_date, effectiveDate: r.effective_date, expiryDate: r.expiry_date,
  status: r.status as FarmerAgreement["status"],
  documentObjectPath: r.document_object_path,
  responsibleStaffId: r.responsible_staff_id,
  capturedAt: r.captured_at, syncedAt: r.synced_at,
  createdAt: r.created_at, updatedAt: r.updated_at, rowVersion: r.row_version,
  farmerName: r.farmer_name,
});

export interface ApiAmendment {
  id: string; project_id: string; original_agreement_id: string;
  amendment_type: string; amendment_reference: string;
  status: string;
  farmer_share_pct?: number; company_share_pct?: number; term_years?: number;
  signed_date?: string; effective_date?: string; expiry_date?: string;
  document_object_path?: string;
  captured_at: string; synced_at?: string;
  created_at: string; updated_at: string; row_version: number;
  planning_only: boolean; original_agreement_reference?: string;
}
export const mapAmendment = (r: ApiAmendment): AgreementAmendment => ({
  id: r.id, projectId: r.project_id, originalAgreementId: r.original_agreement_id,
  amendmentType: r.amendment_type as AgreementAmendment["amendmentType"],
  amendmentReference: r.amendment_reference,
  status: r.status as AgreementAmendment["status"],
  farmerSharePct: r.farmer_share_pct, companySharePct: r.company_share_pct,
  termYears: r.term_years,
  signedDate: r.signed_date, effectiveDate: r.effective_date, expiryDate: r.expiry_date,
  documentObjectPath: r.document_object_path,
  capturedAt: r.captured_at, syncedAt: r.synced_at,
  createdAt: r.created_at, updatedAt: r.updated_at, rowVersion: r.row_version,
  originalAgreementReference: r.original_agreement_reference,
});

// ── Phase 6: Geography mappers ────────────────────────────────

export interface ApiGeographicUnit {
  id: string; name: string; unit_type: string; administrative_level: number;
  parent_id?: string; parent_name?: string; country: string;
  official_code?: string; latitude?: number; longitude?: number;
  status: string; valid_from?: string; valid_until?: string;
  source_name?: string; source_reference?: string;
  source_metadata?: Record<string, unknown>;
}
export const mapGeographicUnit = (r: ApiGeographicUnit): GeographicUnit => ({
  id: r.id, name: r.name, unitType: r.unit_type as GeographicUnit["unitType"],
  administrativeLevel: r.administrative_level, parentId: r.parent_id,
  parentName: r.parent_name, country: r.country, officialCode: r.official_code,
  latitude: r.latitude, longitude: r.longitude, status: r.status,
  validFrom: r.valid_from, validUntil: r.valid_until,
  sourceName: r.source_name, sourceReference: r.source_reference,
  sourceMetadata: r.source_metadata,
});
